<template lang="pug">
AppDropdown.pull-right.table-actions-dropdown(
  v-tooltip
  direction="right"
  label="Actions"
  size="small"
  track
  :disabled="!hasAnyPermissions"
  :title="tooltipTitle"
  data-placement="left"
)
  template(v-if="project.permissions.can_edit_project_basic_details")
    AppDropdownItem(@click="openEditDialog(project.id, 'general')")
      | Edit project
  template(v-else)
    AppDropdownItem(
      v-tooltip="{ placement: 'left' }"
      disabled
      title="You don't have permission to edit this project."
      @click.stop
    )
      | Edit project

  //- Not tracked: the outcome is already tracked server side (membership changes).
  AppDropdownItem(
    v-if="project.permissions.can_manage_project_members"
    :track="false"
    @click="openEditDialog(project.id, 'members')"
  )
    | Manage members

  //- Not tracked: the outcome is already tracked server side (`set_budget`).
  AppDropdownItem(
    v-if="isActiveProject && project.permissions.can_manage_project_budget"
    :track="false"
    @click="openEditDialog(project.id, 'budget')"
  )
    | Edit budget

  template(v-if="project.permissions.can_duplicate_project")
    //- Not tracked: opening the dialog already sends `project_dup_start`.
    AppDropdownItem(
      :track="false"
      @click="duplicateProject(project.id)"
    )
      | Duplicate project
  template(v-else)
    AppDropdownItem.duplicate(
      v-tooltip="{ placement: 'left' }"
      disabled
      title="You don't have permission to duplicate this project."
      @click.stop
    )
      | Duplicate project

  template(v-if="project.permissions.project_can_be_archived")
    template(v-if="canArchive")
      AppDropdownItem(@click="archiveProject(project.id)")
        | Archive project
    template(v-else)
      AppDropdownItem.archive(
        v-tooltip="{ placement: 'left' }"
        disabled
        title="You don't have permission to archive this project."
        @click.stop
      )
        | Archive project

  template(v-if="!isActiveProject")
    template(v-if="project.permissions.project_can_be_restored")
      AppDropdownItem(@click="onRestoreProject(project.id)")
        | Restore project
    template(v-else)
      AppDropdownItem.restore(
        v-tooltip="{ placement: 'left' }"
        disabled
        title="You don't have permission to restore this project."
        @click.stop
      )
        | Restore project

  template(v-if="project.permissions.project_is_transferable")
    template(v-if="project.permissions.can_transfer_project")
      AppDropdownItem(
        track-as="Transfer project"
        @click="transferProject(project.id)"
      )
        | Transfer
    template(v-else)
      AppDropdownItem.transfer(
        v-tooltip="{ placement: 'left' }"
        disabled
        title="You don't have permission to transfer this project."
        @click.stop
      )
        | Transfer
  template(v-else)
    AppDropdownItem.transfer(
      v-tooltip="{ placement: 'left' }"
      disabled
      :title="disabledProjectTransferHint"
      @click.stop
    )
      | Transfer

  template(v-if="!isLinkedViaAddon")
    template(v-if="project.linked_with_tasks")
      template(v-if="project.permissions.can_link_or_unlink_project")
        AppDropdownItem(@click="onUnlinkProject(project.id)")
          | Unlink project
      template(v-else)
        AppDropdownItem.unlink(
          v-tooltip="{ placement: 'left' }"
          disabled
          title="You don't have permission to unlink this project."
          @click.stop
        )
          | Unlink project

    template(v-else-if="project.permissions.project_is_linkable")
      template(v-if="project.permissions.can_link_or_unlink_project")
        template(v-if="hasTeamsSyncFlag() && managedByUnlinkedTeam(project)")
          AppDropdownItem.link(
            v-tooltip="{ placement: 'left' }"
            disabled
            title="You cannot link project because it's managed by unlinked team"
            @click.stop
          )
            | Link project
        template(v-else)
          AppDropdownItem(@click="linkProjectWithTasks(project.id)")
            | Link project
      template(v-else)
        AppDropdownItem.link(
          v-tooltip="{ placement: 'left' }"
          disabled
          title="You don't have permission to link this project."
          @click.stop
        )
          | Link project

  template(v-if="project.permissions.project_can_be_deleted")
    template(v-if="canDelete && showDeleteBtn")
      AppDropdownItem(@click="openDeleteDialog(project)")
        | Delete project
    template(v-else)
      AppDropdownItem.delete(
        v-tooltip="{ placement: 'left' }"
        disabled
        title="You don't have permission to delete this project."
        @click.stop
      )
        | Delete project
</template>

<script>
import { ACTIVE_PROJECT_STATUS, ARCHIVED_PROJECT_STATUS } from '../../app_constants';
import { ProjectsResource } from '~/resources';
import { mapActions, mapState, mapGetters } from 'vuex';

export default {
  name: 'ProjectsTableActions',
  props: {
    project: {
      type: Object,
      required: true,
    },
  },
  emits: ['open-edit-dialog'],
  computed: {
    ...mapState({
      organization: 'organization',
      canArchive: 'can_archive',
      canDelete: 'can_delete',
    }),
    ...mapGetters(['hasTransferProjectsFeature', 'isLinkedViaAddon']),
    isActiveProject() {
      return this.project.status === ACTIVE_PROJECT_STATUS;
    },
    isArchivedProject() {
      return this.project.status === ARCHIVED_PROJECT_STATUS;
    },
    // Show `Delete` btn when
    //   - project is unlinked
    //   - project is archived and linked via addon
    showDeleteBtn() {
      return !this.project.linked_with_tasks || (this.isArchivedProject && this.isLinkedViaAddon);
    },
    hasAnyPermissions() {
      const permissions = this.project.permissions;

      return (
        permissions.can_edit_project_basic_details || // edit button
        permissions.can_manage_project_members || // manage members
        permissions.can_manage_project_budget || // edit budget
        permissions.can_duplicate_project || // duplicate
        (permissions.project_can_be_archived && this.canArchive) || // archive
        (permissions.project_can_be_restored && !this.isActiveProject) || // restore
        (permissions.project_is_transferable && permissions.can_transfer_project) || // transfer
        (permissions.project_is_linkable && permissions.can_link_or_unlink_project) || // link
        (permissions.project_can_be_deleted && this.canDelete && this.showDeleteBtn)
      ); // delete
    },
    tooltipTitle() {
      return this.hasAnyPermissions ? '' : "You don't have permission to perform any actions on this project.";
    },
    disabledProjectTransferHint() {
      if (!this.hasTransferProjectsFeature) {
        return 'This feature is available for paid plans only';
      }

      if (!this.organization.active) {
        return 'Organization must be active in order to transfer this project';
      }

      if (!this.isActiveProject) {
        return 'Project must be active in order to transfer it';
      }

      if (this.project.transfer_in_progress) {
        return 'Another transfer is currently in progress. Please wait for the current transfer to complete';
      }

      if (this.project.linked_with_tasks) {
        return "You can't transfer a project that is linked to Hubstaff Tasks. You need to unlink it first";
      }

      if (this.project.has_client) {
        return `Project cannot be transferred. ${this.project.name} is attached to a client`;
      }

      return 'Project is currently connected to an integration. Transfer is unavailable until you disconnect it from the integration';
    },
  },
  methods: {
    ...mapActions(['loadTable', 'restoreProject', 'unlinkProject', 'setTableLoading']),
    openEditDialog(projectId, tab) {
      this.$emit('open-edit-dialog', projectId, tab);
    },
    archiveProject(id) {
      AjaxDialog.run(ProjectsResource.archiveDialogLink({ id }));
    },
    openDeleteDialog(project) {
      AjaxDialog.run(ProjectsResource.deleteDialogLink({ id: project.id }));
    },
    duplicateProject(id) {
      AjaxDialog.run(ProjectsResource.duplicateDialogLink({ id }));
    },
    linkProjectWithTasks(id) {
      AjaxDialog.run(ProjectsResource.linkDialogLink({ id }));
    },
    transferProject(id) {
      AjaxDialog.run(ProjectsResource.transferDialogLink({ id }));
    },
    onUnlinkProject(id) {
      if (confirm('Unlink this project? This project will no longer sync with Hubstaff Tasks.')) {
        this.setTableLoading(true);

        const success = (response) => {
          if (response && response.success) {
            this.$appMessage('Project successfully unlinked');
            this.loadTable();
          } else {
            const errorMessage = response && response.errors ? response.errors : 'Could not unlink project';

            this.$appMessage(errorMessage);
            this.setTableLoading(false);
          }
        };

        this.unlinkProject(id).then(success, this.showUnexpectedError);
      }
    },
    onRestoreProject(id) {
      this.setTableLoading(true);

      const success = (response) => {
        if (response && response.success) {
          this.$appMessage('Restored project');
          this.loadTable();
        } else {
          this.$appMessage('Unable to restore project');
          this.setTableLoading(false);
        }
      };

      this.restoreProject(id).then(success, this.showUnexpectedError);
    },
    showUnexpectedError() {
      this.$appMessage('Unexpected error. Please try again.');
    },
    managedByUnlinkedTeam(project) {
      return project.teams_data.all_count !== project.teams_data.linked_count;
    },
    hasTeamsSyncFlag() {
      return this.organization.flags.includes('teams_sync');
    },
  },
};
</script>
